import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import type { PERCENT_SOLUTION_CALCULATOR_STRINGS } from "@/components/tools/percent-solution-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { formatPlain, formatSig } from "@/lib/tool-kit/chemistry/format";
import {
  gramsPerLitre,
  type PercentResult,
  type PercentSolve,
  percentToMolarity,
} from "@/lib/tool-kit/chemistry/percent";
import type { PercentKind } from "@/lib/tool-kit/chemistry/solutions";
import {
  findUnit,
  fromBase,
  MASS,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";

type Strings = typeof PERCENT_SOLUTION_CALCULATOR_STRINGS;
type StringKey = {
  [K in keyof Strings]: Strings[K] extends string ? K : never;
}[keyof Strings];

export interface PercentUnits {
  solute: string;
  solution: string;
}

/** The unit family and base unit of the solute and the solution for each kind. */
const KIND_UNITS = {
  wv: { solute: "g", solution: "mL" },
  ww: { solute: "g", solution: "g" },
  vv: { solute: "mL", solution: "mL" },
} as const;

/** mL values are stored as litres in VOLUME; mass in grams in MASS. */
export function fromBaseAmount(
  value: number,
  kind: PercentKind,
  part: "solute" | "solution",
  unitId: string
): number {
  const isMass = part === "solute" ? kind !== "vv" : kind === "ww";
  return isMass
    ? fromBase(value, MASS, unitId)
    : fromBase(value / 1000, VOLUME, unitId);
}

export function unitSymbol(
  kind: PercentKind,
  part: "solute" | "solution",
  unitId: string
): string {
  const isMass = part === "solute" ? kind !== "vv" : kind === "ww";
  return findUnit(isMass ? MASS : VOLUME, unitId).symbol;
}

const KIND_SUFFIX: Record<PercentKind, StringKey> = {
  wv: "suffixWv",
  ww: "suffixWw",
  vv: "suffixVv",
};

const FORMULA: Record<PercentKind, StringKey> = {
  wv: "formulaWv",
  ww: "formulaWw",
  vv: "formulaVv",
};

const RECIPE: Record<PercentKind, StringKey> = {
  wv: "recipeWv",
  ww: "recipeWw",
  vv: "recipeVv",
};

export function PercentAnswer({
  kind,
  solve,
  r,
  units,
  sig,
  molarity,
  strings,
}: {
  kind: PercentKind;
  solve: PercentSolve;
  r: PercentResult;
  units: PercentUnits;
  sig: number;
  molarity: { molarMass: number; density: number | null } | null;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const base = KIND_UNITS[kind];
  const suffix = strings[KIND_SUFFIX[kind]];
  const soluteText = `${formatSig(
    fmt,
    fromBaseAmount(r.solute, kind, "solute", units.solute),
    sig
  )} ${unitSymbol(kind, "solute", units.solute)}`;
  const solutionText = `${formatSig(
    fmt,
    fromBaseAmount(r.solution, kind, "solution", units.solution),
    sig
  )} ${unitSymbol(kind, "solution", units.solution)}`;
  const percentText = `${formatSig(fmt, r.percent, sig)}% ${suffix}`;
  const answer = {
    percent: { label: strings.answerPercent, value: percentText },
    solute: { label: strings.answerSolute, value: soluteText },
    solution: { label: strings.answerSolution, value: solutionText },
  }[solve];
  const equation = t(strings[FORMULA[kind]], {});
  const substituted = t(strings.substituted, {
    percent: formatPlain(fmt, r.percent),
    solute: formatPlain(fmt, r.solute),
    solution: formatPlain(fmt, r.solution),
    soluteUnit: base.solute,
    solutionUnit: base.solution,
  });
  const solventText =
    r.solvent === null
      ? ""
      : `${formatSig(fmt, r.solvent, sig)} ${kind === "ww" ? "g" : "mL"}`;
  const perLitre = gramsPerLitre({
    kind,
    percent: r.percent,
    density: molarity?.density ?? null,
  });
  const molar =
    molarity === null
      ? null
      : percentToMolarity({
          kind,
          percent: r.percent,
          molarMass: molarity.molarMass,
          density: molarity.density,
        });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={answer.label} value={answer.value} />
        {solve === "percent" ? null : (
          <StatCard label={strings.answerPercent} value={percentText} />
        )}
        {solve === "solute" ? null : (
          <StatCard label={strings.answerSolute} value={soluteText} />
        )}
        {solve === "solution" ? null : (
          <StatCard label={strings.answerSolution} value={solutionText} />
        )}
        {molar === null ? null : (
          <StatCard
            label={strings.molarity}
            value={`${formatSig(fmt, molar, sig)} mol/L`}
          />
        )}
      </StatGrid>
      {molarity !== null && molar === null ? (
        <Notice>
          {kind === "ww"
            ? strings.needSolutionDensity
            : strings.needSoluteDensity}
        </Notice>
      ) : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{equation}</p>
        <p>{substituted}</p>
        {perLitre !== null && molarity !== null && molar !== null ? (
          <>
            <p>
              {t(strings.gramsPerLitreLine, {
                expression:
                  kind === "wv"
                    ? `${formatPlain(fmt, r.percent)} × 10`
                    : `${formatPlain(fmt, r.percent)} × 10 × ${formatPlain(fmt, molarity.density ?? 0)}`,
                grams: formatSig(fmt, perLitre, sig + 1),
              })}
            </p>
            <p>
              {t(strings.molarityLine, {
                grams: formatSig(fmt, perLitre, sig + 1),
                molar: formatPlain(fmt, molarity.molarMass),
                result: formatSig(fmt, molar, sig),
              })}
            </p>
          </>
        ) : null}
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.recipeHeading}</h3>
        <p className="text-foreground text-sm leading-relaxed">
          {t(strings[RECIPE[kind]], {
            solute: soluteText,
            solution: solutionText,
            solvent: solventText,
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          percent: percentText,
          solute: soluteText,
          solution: solutionText,
        })}
      />
    </>
  );
}
